<template>
  <div class="content-main">
    <!-- Product Detail Modal -->
    <BaseModal v-model="showDetailModal" @close="closeDetail">
      <div
        class="p-4 sm:p-6"
        style="width: 600px; max-width: 100%; max-height: 80vh; overflow-y: auto"
      >
        <div class="flex justify-between items-start mb-4">
          <div>
            <h3 class="text-lg font-semibold">{{ detailProduct.nkrCode }}</h3>
            <p class="text-sm text-gray-500">
              {{ detailProduct.name }} — {{ detailProduct.brand }}
            </p>
          </div>
          <button
            class="text-blue-600 text-sm hover:underline"
            @click="editMode = !editMode"
          >
            {{ editMode ? "Cancel Edit" : "Edit" }}
          </button>
        </div>

        <!-- Edit form -->
        <div v-if="editMode" class="mb-4 p-4 bg-gray-50 rounded">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium mb-1">Name</label>
              <input
                v-model="editForm.name"
                class="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label class="block text-xs font-medium mb-1">Brand</label>
              <input
                v-model="editForm.brand"
                class="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <div class="col-span-2">
              <label class="block text-xs font-medium mb-1">Description</label>
              <textarea
                v-model="editForm.description"
                class="w-full border rounded px-3 py-2 text-sm"
                rows="2"
              ></textarea>
            </div>
            <div>
              <label class="block text-xs font-medium mb-1">Image URL</label>
              <input
                v-model="editForm.image_url"
                class="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
            <div class="flex items-center gap-2 pt-5">
              <input
                type="checkbox"
                v-model="editForm.active"
                id="editActive"
              />
              <label for="editActive" class="text-sm">Active</label>
            </div>
          </div>
          <div class="flex justify-end gap-2 mt-3">
            <button
              class="px-3 py-1 border rounded text-sm"
              @click="editMode = false"
            >
              Cancel
            </button>
            <button
              class="px-3 py-1 bg-blue-600 text-white rounded text-sm"
              @click="saveProduct"
              :disabled="savingProduct"
            >
              {{ savingProduct ? "Saving..." : "Save" }}
            </button>
          </div>
        </div>

        <!-- Tabs -->
        <div class="flex border-b mb-4 overflow-x-auto">
          <button
            v-for="tab in ['Stock', 'History', 'Lines']"
            :key="tab"
            class="px-4 py-2 text-sm"
            :class="
              detailTab === tab
                ? 'border-b-2 border-blue-600 font-semibold'
                : 'text-gray-500'
            "
            @click="detailTab = tab"
          >
            {{ tab }}
          </button>
        </div>

        <!-- Stock tab -->
        <div v-if="detailTab === 'Stock'" class="overflow-x-auto">
          <table class="default-table" v-if="detailInventory.length > 0">
            <thead>
              <tr class="table100-head">
                <th>Line</th>
                <th>Warehouse</th>
                <th>Quantity</th>
                <th>Location</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="inv in detailInventory"
                :key="inv.productId + '-' + inv.warehouseId"
              >
                <td>{{ inv.line }}</td>
                <td>{{ inv.warehouseCode }}</td>
                <td>{{ inv.quantity }}</td>
                <td>{{ inv.locationId || "—" }}</td>
                <td>
                  <button
                    class="text-blue-600 text-sm hover:underline"
                    @click="openAdjust(inv)"
                  >
                    Adjust
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else class="text-gray-500 text-sm">No inventory data</p>
        </div>

        <!-- History tab -->
        <div v-if="detailTab === 'History'">
          <div class="flex gap-2 mb-3">
            <select
              v-model="historyFilter.movement_type"
              class="border rounded px-2 py-1 text-sm"
              @change="loadDetailHistory"
            >
              <option value="">All Types</option>
              <option value="adjustment">Adjustment</option>
              <option value="dispatch">Dispatch</option>
              <option value="reception">Reception</option>
              <option value="transfer_in">Transfer In</option>
              <option value="transfer_out">Transfer Out</option>
            </select>
            <input
              v-model="historyFilter.date_from"
              type="date"
              class="border rounded px-2 py-1 text-sm"
              @change="loadDetailHistory"
            />
          </div>
          <div class="overflow-x-auto">
            <table class="default-table" v-if="detailHistory.length > 0">
              <thead>
                <tr class="table100-head">
                  <th>Date</th>
                  <th>Type</th>
                  <th>Qty</th>
                  <th>Balance</th>
                  <th>Classification</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in detailHistory" :key="m.id">
                  <td>{{ formatDate(m.createdAt) }}</td>
                  <td>{{ m.movementType }}</td>
                  <td>{{ m.quantity }}</td>
                  <td>{{ m.balanceAfter }}</td>
                  <td>{{ m.classification || "—" }}</td>
                  <td class="text-xs">{{ m.notes || "—" }}</td>
                </tr>
              </tbody>
            </table>
            <p v-else class="text-gray-500 text-sm">No movement history</p>
          </div>
        </div>

        <!-- Lines tab -->
        <div v-if="detailTab === 'Lines'" class="overflow-x-auto">
          <table class="default-table" v-if="detailLines.length > 0">
            <thead>
              <tr class="table100-head">
                <th>Product ID</th>
                <th>Line Code</th>
                <th>Total Stock</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="l in detailLines" :key="l.productId">
                <td>{{ l.productId }}</td>
                <td>{{ l.line }}</td>
                <td>{{ l.quantity }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="text-gray-500 text-sm">No line data</p>
        </div>
      </div>
    </BaseModal>

    <!-- Adjust Stock Modal -->
    <BaseModal v-model="showAdjustModal" @close="showAdjustModal = false">
      <div class="p-4 sm:p-6" style="width: 400px; max-width: 100%">
        <h3 class="text-lg font-semibold mb-4">Adjust Stock</h3>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">Product</label>
          <div class="text-sm text-gray-600">
            {{ adjustTarget?.nkrCode || adjustTarget?.productCode }} —
            {{ adjustTarget?.warehouseCode }}
          </div>
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">Quantity</label>
          <input
            v-model.number="adjustForm.quantity"
            type="number"
            class="w-full border rounded px-3 py-2"
          />
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">Classification</label>
          <select
            v-model="adjustForm.classification"
            class="w-full border rounded px-3 py-2"
          >
            <option value="">-- Select --</option>
            <option value="count_correction">Count Correction</option>
            <option value="lost_stock">Lost Stock</option>
            <option value="damaged">Damaged</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1"
            >Notes <span class="text-red-500">*</span></label
          >
          <textarea
            v-model="adjustForm.notes"
            class="w-full border rounded px-3 py-2"
            rows="2"
            placeholder="Required"
          ></textarea>
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <button
            class="px-4 py-2 border rounded"
            @click="showAdjustModal = false"
          >
            Cancel
          </button>
          <button
            class="px-4 py-2 bg-blue-600 text-white rounded"
            @click="doAdjust"
            :disabled="
              !adjustForm.classification || !adjustForm.notes || adjusting
            "
          >
            {{ adjusting ? "Adjusting..." : "Submit" }}
          </button>
        </div>
      </div>
    </BaseModal>

    <div class="content" style="width: 100%">
      <div class="header flex justify-between items-center">
        Products
        <button
          class="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
          :disabled="syncing"
          @click="syncCatalog"
        >
          {{ syncing ? "Syncing..." : "Sync Catalog" }}
        </button>
      </div>

      <!-- Filters -->
      <div class="flex flex-wrap gap-3 px-4 py-3 items-end">
        <div>
          <label class="block text-xs font-medium mb-1">Search</label>
          <input
            v-model="filters.search"
            type="text"
            class="border rounded px-3 py-2 text-sm"
            placeholder="Code, name, brand..."
            @input="debouncedLoad"
          />
        </div>
        <div>
          <label class="block text-xs font-medium mb-1">Departamento</label>
          <select
            v-model="filters.departamento_id"
            class="border rounded px-3 py-2 text-sm"
            @change="loadProducts"
          >
            <option value="">All</option>
            <option v-for="d in departamentos" :key="d.id" :value="d.id">{{
              d.nombre
            }}</option>
          </select>
        </div>
        <div class="flex items-center gap-2 pt-5">
          <input
            type="checkbox"
            id="activeOnly"
            v-model="filters.active"
            @change="loadProducts"
          />
          <label for="activeOnly" class="text-sm">Active only</label>
        </div>
      </div>

      <!-- Table -->
      <div class="table" style="width: 100%">
        <table class="default-table w-full" v-if="!loading">
          <thead>
            <tr class="table100-head">
              <th @click="changeSort('nkr_code')">NKR Code</th>
              <th @click="changeSort('name')">Name</th>
              <th @click="changeSort('brand')">Brand</th>
              <th>Marca Fab</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="product in products"
              :key="product.nkrCode"
              @click="openDetail(product)"
              class="cursor-pointer hover:bg-blue-50"
            >
              <td>{{ product.nkrCode }}</td>
              <td>{{ product.name }}</td>
              <td>{{ product.brand }}</td>
              <td>{{ product.marcaFab }}</td>
              <td>
                <span
                  class="px-2 py-1 text-xs rounded"
                  :class="
                    product.active == 1
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-500'
                  "
                >
                  {{ product.active == 1 ? "Yes" : "No" }}
                </span>
              </td>
            </tr>
            <tr v-if="products.length === 0">
              <td colspan="5" class="text-center text-gray-500 py-4">
                No products found
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="p-8 text-center">Loading...</div>
      </div>

      <!-- Pagination -->
      <div class="footer">
        <div class="totals">
          showing {{ perPage * (page - 1) + 1 }} to
          {{ Math.min(perPage * page, totalItems) }} from
          {{ totalItems }} results
        </div>
        <div class="pages">
          <select
            v-model="perPage"
            @change="
              page = 1;
              loadProducts();
            "
          >
            <option :value="20">20</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
          Per Page
        </div>
        <div class="pages">
          <a
            class="page-link"
            :class="{ disabled: page <= 1 }"
            @click="previous"
            >Previous</a
          >
          <span>Page {{ page }}</span>
          <a
            class="page-link"
            :class="{ disabled: page >= totalPages }"
            @click="next"
            >Next</a
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getProducts,
  getProduct,
  updateProduct,
  getInventoryByProduct,
  getMovementHistory,
  adjustStock,
  catalogSync
} from "@/api/Products.js";

export default {
  name: "ProductsPage",
  data() {
    return {
      products: [],
      loading: false,
      page: 1,
      perPage: 20,
      totalItems: 0,
      sortCol: "nkr_code",
      sortDir: "asc",
      filters: {
        search: "",
        departamento_id: "",
        active: true
      },
      debounceTimer: null,
      // Detail modal
      showDetailModal: false,
      detailProduct: {},
      detailTab: "Stock",
      detailInventory: [],
      detailHistory: [],
      detailLines: [],
      historyFilter: { movement_type: "", date_from: "" },
      editMode: false,
      editForm: {
        name: "",
        description: "",
        brand: "",
        image_url: "",
        active: true
      },
      savingProduct: false,
      // Adjust modal
      showAdjustModal: false,
      adjustTarget: null,
      adjustForm: { quantity: 0, classification: "", notes: "" },
      adjusting: false,
      syncing: false
    };
  },
  computed: {
    departamentos() {
      return this.$store.getters["general/getDepartments"] || [];
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.perPage) || 1;
    }
  },
  mounted() {
    this.loadProducts();
  },
  methods: {
    async loadProducts() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          limit: this.perPage,
          sort: this.sortCol,
          sort_dir: this.sortDir
        };
        if (this.filters.search) params.search = this.filters.search;
        if (this.filters.departamento_id)
          params.departamento_id = this.filters.departamento_id;
        if (this.filters.active) params.active = 1;
        const data = await getProducts(params);
        this.products = data.items || [];
        this.totalItems = data.total || this.products.length;
      } finally {
        this.loading = false;
      }
    },
    debouncedLoad() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.page = 1;
        this.loadProducts();
      }, 300);
    },
    changeSort(col) {
      if (this.sortCol === col) {
        this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      } else {
        this.sortCol = col;
        this.sortDir = "asc";
      }
      this.loadProducts();
    },
    async openDetail(product) {
      this.detailProduct = product;
      this.detailTab = "Stock";
      this.editMode = false;
      this.showDetailModal = true;
      await Promise.all([
        this.loadDetailInventory(),
        this.loadDetailHistory(),
        this.loadDetailLines()
      ]);
    },
    closeDetail() {
      this.showDetailModal = false;
      this.detailProduct = {};
      this.detailInventory = [];
      this.detailHistory = [];
      this.detailLines = [];
    },
    async loadDetailInventory() {
      try {
        const params = {};
        if (this.filters.departamento_id)
          params.departamento_id = this.filters.departamento_id;
        const data = await getInventoryByProduct(
          this.detailProduct.nkrCode,
          params
        );
        this.detailInventory = data.data || data || [];
      } catch {
        this.detailInventory = [];
      }
    },
    async loadDetailHistory() {
      try {
        const params = { nkr_code: this.detailProduct.nkrCode };
        if (this.historyFilter.movement_type)
          params.movement_type = this.historyFilter.movement_type;
        if (this.historyFilter.date_from)
          params.date_from = this.historyFilter.date_from;
        const data = await getMovementHistory(params);
        this.detailHistory = data.data || data || [];
      } catch {
        this.detailHistory = [];
      }
    },
    async loadDetailLines() {
      try {
        const data = await getProduct(this.detailProduct.nkrCode, {
          include_lines: true
        });
        this.detailLines = data.lines || [];
      } catch {
        this.detailLines = [];
      }
    },
    async saveProduct() {
      this.savingProduct = true;
      try {
        await updateProduct(this.detailProduct.id, this.editForm);
        this.editMode = false;
        this.loadProducts();
      } catch {
        alert("Failed to save product");
      } finally {
        this.savingProduct = false;
      }
    },
    openAdjust(inv) {
      this.adjustTarget = inv;
      this.adjustForm = { quantity: 0, classification: "", notes: "" };
      this.showAdjustModal = true;
    },
    async doAdjust() {
      this.adjusting = true;
      try {
        await adjustStock({
          product_id: this.adjustTarget.productId,
          warehouse_id: this.adjustTarget.warehouseId,
          departamento_id: this.filters.departamento_id || undefined,
          adjustment_qty: this.adjustForm.quantity,
          classification: this.adjustForm.classification,
          notes: this.adjustForm.notes
        });
        this.showAdjustModal = false;
        await this.loadDetailInventory();
      } catch {
        alert("Adjustment failed");
      } finally {
        this.adjusting = false;
      }
    },
    formatDate(d) {
      return d ? new Date(d).toLocaleString() : "—";
    },
    async syncCatalog() {
      this.syncing = true;
      try {
        await catalogSync();
        await this.loadProducts();
      } catch {
        alert("Catalog sync failed");
      } finally {
        this.syncing = false;
      }
    },
    previous() {
      if (this.page > 1) {
        this.page--;
        this.loadProducts();
      }
    },
    next() {
      if (this.page < this.totalPages) {
        this.page++;
        this.loadProducts();
      }
    }
  }
};
</script>
